import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { UploadCloud, Loader2 } from "lucide-react";
import { logProjectActivity } from "../utils/activityLogger";
import apiClient from "../utils/apiClient";
import PrimaryButton from "../ui/PrimaryButton";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import ErrorBanner from "../common/ErrorBanner";
import FileUploadZone from "../uploads/FileUploadZone";
import ToastNotification from "../common/ToastNotification";

const LoadingSpinner = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center space-y-4">
      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      <p className="text-gray-700 font-medium">Importing sensors...</p>
    </div>
  </div>
);

const SensorCount = ({ count }) => (
  <div className="px-6 py-4">
    <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
      <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
        Current Sensors
      </p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{count}</p>
      <p className="mt-1 text-sm text-gray-500">
        Active virtual sensors in this project
      </p>
    </div>
  </div>
);

const FileRequirements = () => (
  <div className="mt-8 bg-gray-50 rounded-lg p-6">
    <h2 className="text-sm font-medium text-gray-900 mb-4">
      File Requirements
    </h2>
    <ul className="space-y-2">
      <li className="flex items-start text-sm text-gray-600">
        <span className="mr-2">•</span>
        CSV file with UTM coordinates and sensor data
      </li>
      <li className="flex items-start text-sm text-gray-600">
        <span className="mr-2">•</span>
        Required columns: UTM, sun_hours, direct_energy_intake,
        diffuse_energy_intake, global_energy_intake
      </li>
      <li className="flex items-start text-sm text-gray-600">
        <span className="mr-2">•</span>
        UTM format should be in X_Y_Z format
      </li>
      <li className="flex items-start text-sm text-gray-600">
        <span className="mr-2">•</span>
        All energy values should be in kWh/m²
      </li>
    </ul>
  </div>
);

const VirtualWholeSensorSetup = () => {
  const { projectId } = useParams();

  const [fileData, setFileData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [fileName, setFileName] = useState("No file chosen");
  const [sensorsCount, setSensorsCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchSensorCount();
  }, [projectId]);

  const fetchSensorCount = async () => {
    try {
      const data = await apiClient.get("/api/sensorslight/count/", {
        params: { project_id: projectId },
        showToast: false,
      });

      setSensorsCount(data.count);
      setError(null);
    } catch (err) {
      console.error("Error fetching sensor count:", err);
      setError("Failed to load sensor count. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type, duration = 3000) => {
    setToast({
      show: true,
      message,
      type,
    });

    if (type !== "loading") {
      setTimeout(() => {
        hideToast();
      }, duration);
    }
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;

    const file = files[0];
    setFileName(file.name);
    setFileData(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!fileData) {
      setSubmitStatus("error");
      showToast("Please select a file to upload", "error");
      await logProjectActivity(
        "VIRTUAL_SENSOR_UPLOAD",
        400,
        "Failed to upload virtual sensors - No file selected",
        "Virtual Sensor Setup"
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    showToast("Importing virtual sensors. Please wait...", "loading");

    try {
      const data = await apiClient.uploadFile(
        "/api/import-sensors",
        fileData,
        { project_id: projectId },
        {
          useOperational: true,
          showToast: false,
        }
      );

      if (data.message === "Data imported successfully") {
        showToast("Virtual sensors imported successfully!", "success");
        setSubmitStatus("success");
        setFileData(null);
        setFileName("No file chosen");
        await fetchSensorCount();

        await logProjectActivity(
          "VIRTUAL_SENSOR_UPLOAD",
          200,
          `Successfully imported virtual sensors from ${fileName}`,
          "Virtual Sensor Setup"
        );
      } else {
        throw new Error(data.error || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showToast("Error importing virtual sensors. Please try again.", "error");
      setSubmitStatus("error");

      await logProjectActivity(
        "VIRTUAL_SENSOR_UPLOAD",
        500,
        `Failed to import virtual sensors: ${error.message}`,
        "Virtual Sensor Setup"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      {isSubmitting && <LoadingSpinner />}

      <ToastNotification
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
        position="top-right"
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Virtual Sensor Configuration - Project ${projectId}`}
            subtitle="Configure virtual sensors for environmental monitoring"
          >
            <ErrorBanner message={error} />

            {!loading && sensorsCount !== null && (
              <SensorCount count={sensorsCount} />
            )}

            <div className="p-6">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <SectionHeader title="Virtual Sensor Data File" />

                  <FileUploadZone
                    onFilesSelect={handleFilesSelect}
                    disabled={isSubmitting}
                    accept=".csv"
                    acceptedTypes={["CSV"]}
                  />
                </div>

                <PrimaryButton
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={!fileData}
                  loadingText="Importing Sensors..."
                >
                  Import Virtual Sensors
                </PrimaryButton>
              </form>

              <FileRequirements />
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default VirtualWholeSensorSetup;
